import type { PreviewSandboxCandidate, ProjectPreviewAvailability, ProjectPreviewTarget } from './const';
import { useCallback } from 'react';
import {
  beginPreviewRefresh,
  refreshPreviewIframe,
  setSandboxPreviewUrl,
  setStablePreviewUrl,
  setTargetLayer,
  tracePreview,
} from '@/helpers/preview/helper';
import { previewStartupTracker } from '@/helpers/preview/previewStartupTrackingRuntime';
import { getLatestVersionPreviewUrl } from '@/helpers/project/preRelease';
import { usePreviewStore } from '@/stores/preview';
import { Stage, useProjectStore } from '@/stores/project';
import { queryProjectSandbox, resetSandboxEnsure } from '../sandbox';
import { PREVIEW_REFRESH_COOLDOWN_MS } from './const';

let refreshRevision = 0;
const pendingPreviewRefreshes = new Map<string, Promise<void>>();
const previewRefreshTimestamps = new Map<string, number>();

export function resolveProjectPreviewTarget<TSandbox extends PreviewSandboxCandidate>(
  availability: ProjectPreviewAvailability<TSandbox>,
  currentStableUrl = '',
): ProjectPreviewTarget<TSandbox> {
  if (availability.sandbox?.status === 'running' && availability.sandbox.url) {
    return { type: 'live', sandbox: availability.sandbox };
  }

  const stableUrl = availability.stableUrl || currentStableUrl;
  return stableUrl ? { type: 'stable', stableUrl } : { type: 'error' };
}

export async function getProjectPreviewAvailability(projectId: string): Promise<ProjectPreviewAvailability> {
  tracePreview('availability.requested', { projectId });
  const [sandbox, stableUrl] = await Promise.all([
    queryProjectSandbox(projectId),
    getLatestVersionPreviewUrl(projectId),
  ]);
  const availability = { sandbox, stableUrl };
  tracePreview('availability.received', {
    projectId,
    sandboxStatus: sandbox?.status ?? 'none',
    hasSandboxUrl: Boolean(sandbox?.url),
    hasStableUrl: Boolean(stableUrl),
  });
  return availability;
}

export function beginProjectPreviewRefresh() {
  resetSandboxEnsure();
  beginPreviewRefresh();
}

export function applyProjectPreviewAvailability(
  projectId: string,
  availability: ProjectPreviewAvailability,
): 'live' | 'stable' | 'error' | null {
  if (useProjectStore.getState().projectId !== projectId) {
    tracePreview('availability.ignored', { projectId, reason: 'project-changed' });
    return null;
  }

  const projectState = useProjectStore.getState();
  const previewState = usePreviewStore.getState();
  if (previewState.deliveryExpectedVersionId) {
    tracePreview('availability.ignored', {
      projectId,
      reason: 'delivery-preview-active',
    });
    return previewState.activePreviewSource === 'sandbox' ? 'live' : 'stable';
  }
  const target = resolveProjectPreviewTarget(availability, previewState.preReleaseUrl);
  tracePreview('availability.target-resolved', { projectId, target: target.type });
  if (availability.stableUrl) {
    setStablePreviewUrl(availability.stableUrl);
    previewState.setPreReleaseReady(true);
  }

  if (target.type === 'live') {
    projectState.setSandboxId(target.sandbox.sandboxId);
    setTargetLayer('live');
    previewState.setSandboxRuntimeStatus('recovering');
    previewState.setLivePreviewProblem(undefined);
    if (previewState.sandboxUrl === target.sandbox.url) {
      refreshPreviewIframe('live');
    } else {
      setSandboxPreviewUrl(target.sandbox.url);
    }
    return 'live';
  }

  projectState.setSandboxId('');
  projectState.setSandboxStatus('unavailable');
  setSandboxPreviewUrl('');
  previewState.setSandboxRuntimeStatus('sleeping');
  setTargetLayer('stable');

  if (target.type === 'stable') {
    if (previewState.preReleaseUrl !== target.stableUrl) {
      setStablePreviewUrl(target.stableUrl);
      previewState.setPreReleaseReady(true);
    }
    previewState.setLivePreviewStatus('idle');
    previewState.setLivePreviewProblem(undefined);
    refreshPreviewIframe('stable');
    previewStartupTracker.fail('sandbox_unavailable');
    return 'stable';
  }

  previewState.setLivePreviewStatus('error');
  previewState.setLivePreviewProblem('sandbox-unavailable');
  previewStartupTracker.fail('no_available_preview');
  return 'error';
}

async function performProjectPreviewRefresh(projectId: string): Promise<void> {
  const revision = ++refreshRevision;
  tracePreview('refresh.started', { projectId, revision });
  useProjectStore.getState().setErrorMessage('');
  useProjectStore.getState().setStage(Stage.Ready);
  beginProjectPreviewRefresh();
  const availability = await getProjectPreviewAvailability(projectId);
  if (revision !== refreshRevision) {
    tracePreview('refresh.ignored', { projectId, revision, reason: 'newer-refresh' });
    return;
  }
  applyProjectPreviewAvailability(projectId, availability);
  tracePreview('refresh.completed', { projectId, revision });
}

export function reloadProjectPreview(projectId: string): Promise<void> {
  if (!projectId) {
    return Promise.resolve();
  }

  const pendingRefresh = pendingPreviewRefreshes.get(projectId);
  if (pendingRefresh) {
    tracePreview('refresh.reused', { projectId });
    return pendingRefresh;
  }

  const refresh = performProjectPreviewRefresh(projectId).finally(() => {
    if (pendingPreviewRefreshes.get(projectId) === refresh) {
      pendingPreviewRefreshes.delete(projectId);
    }
  });
  pendingPreviewRefreshes.set(projectId, refresh);
  return refresh;
}

function refreshProjectPreviewFromAction(projectId: string): Promise<void> {
  if (!projectId) {
    return Promise.resolve();
  }

  const pendingRefresh = pendingPreviewRefreshes.get(projectId);
  if (pendingRefresh) {
    tracePreview('refresh.action-reused', { projectId });
    return pendingRefresh;
  }

  const now = Date.now();
  const lastRefreshAt = previewRefreshTimestamps.get(projectId) ?? 0;
  if (now - lastRefreshAt < PREVIEW_REFRESH_COOLDOWN_MS) {
    tracePreview('refresh.action-skipped', { projectId, reason: 'cooldown' });
    return Promise.resolve();
  }

  previewRefreshTimestamps.set(projectId, now);
  tracePreview('refresh.action-triggered', { projectId });
  return reloadProjectPreview(projectId);
}

export function useRefreshProjectPreview() {
  const projectId = useProjectStore(state => state.projectId);

  return useCallback(() => refreshProjectPreviewFromAction(projectId), [projectId]);
}
